import React, { useMemo, useState } from "react";
import {
    Search,
    Plus,
    RotateCcw,
    Edit3,
    Eye,
    Package,
    ChevronLeft,
    ChevronRight,
} from "lucide-react";

import "../styles/Services.css";

const serviceData = [
    {
        id: "SER001",
        name: "Packing Service",
        category: "Packing",
        description: "Complete packing of household items.",
        pricingType: "Fixed",
        price: 5000,
        status: "ACTIVE",
    },
    {
        id: "SER002",
        name: "Unpacking Service",
        category: "Packing",
        description: "Unpacking and arranging household items.",
        pricingType: "Fixed",
        price: 3500,
        status: "ACTIVE",
    },
    {
        id: "SER003",
        name: "Loading Service",
        category: "Moving",
        description: "Professional loading service.",
        pricingType: "Fixed",
        price: 2500,
        status: "ACTIVE",
    },
    {
        id: "SER004",
        name: "Unloading Service",
        category: "Moving",
        description: "Professional unloading service.",
        pricingType: "Fixed",
        price: 2500,
        status: "ACTIVE",
    },
    {
        id: "SER005",
        name: "Furniture Dismantling",
        category: "Furniture",
        description: "Dismantling furniture before moving.",
        pricingType: "Starting From",
        price: 1500,
        status: "ACTIVE",
    },
    {
        id: "SER006",
        name: "Furniture Assembly",
        category: "Furniture",
        description: "Reassemble furniture at destination.",
        pricingType: "Starting From",
        price: 1800,
        status: "ACTIVE",
    },
    {
        id: "SER007",
        name: "Vehicle Transportation",
        category: "Transportation",
        description: "Safe transportation of two-wheelers.",
        pricingType: "Starting From",
        price: 4500,
        status: "ACTIVE",
    },
    {
        id: "SER008",
        name: "Storage Service",
        category: "Storage",
        description: "Short and long-term storage facility.",
        pricingType: "Per Month",
        price: 3000,
        status: "INACTIVE",
    },
];

const categoryOptions = [
    "ALL",
    "Packing",
    "Moving",
    "Furniture",
    "Transportation",
    "Storage",
];

const formatAmount = (amount) => {
    return `₹${amount.toLocaleString("en-IN")}`;
};

export default function Services() {
    const [search, setSearch] = useState("");
    const [category, setCategory] = useState("ALL");
    const [status, setStatus] = useState("ALL");
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(5);

    const filteredServices = useMemo(() => {
        return serviceData.filter((service) => {
            const value = search.toLowerCase().trim();

            const matchesSearch =
                !value ||
                service.id.toLowerCase().includes(value) ||
                service.name.toLowerCase().includes(value) ||
                service.category.toLowerCase().includes(value);

            const matchesCategory =
                category === "ALL" ||
                service.category === category;

            const matchesStatus =
                status === "ALL" ||
                service.status === status;

            return (
                matchesSearch &&
                matchesCategory &&
                matchesStatus
            );
        });
    }, [search, category, status]);

    const totalPages = Math.max(
        1,
        Math.ceil(
            filteredServices.length / rowsPerPage
        )
    );

    const currentPage = Math.min(
        page,
        totalPages
    );

    const paginatedServices = filteredServices.slice(
        (currentPage - 1) * rowsPerPage,
        currentPage * rowsPerPage
    );

    const clearFilters = () => {
        setSearch("");
        setCategory("ALL");
        setStatus("ALL");
        setPage(1);
    };

    return (
        <div className="services-page">

            {/* Header */}
            <div className="services-page-header">

                <div>
                    <h1>Services</h1>

                    <p>
                        Manage moving and additional services.
                    </p>
                </div>

                <button className="add-service-btn">
                    <Plus size={17} />
                    Add Service
                </button>

            </div>


            {/* Summary */}
            <div className="services-summary">

                <div className="service-summary-card">

                    <div className="service-summary-icon">
                        <Package size={19} />
                    </div>

                    <div>
                        <strong>
                            {serviceData.length}
                        </strong>

                        <span>
                            Total Services
                        </span>
                    </div>

                </div>


                <div className="service-summary-card">

                    <div className="service-summary-icon active">
                        <Package size={19} />
                    </div>

                    <div>
                        <strong>
                            {
                                serviceData.filter(
                                    (item) =>
                                        item.status ===
                                        "ACTIVE"
                                ).length
                            }
                        </strong>

                        <span>
                            Active Services
                        </span>
                    </div>

                </div>


                <div className="service-summary-card">

                    <div className="service-summary-icon inactive">
                        <Package size={19} />
                    </div>

                    <div>
                        <strong>
                            {
                                serviceData.filter(
                                    (item) =>
                                        item.status ===
                                        "INACTIVE"
                                ).length
                            }
                        </strong>

                        <span>
                            Inactive Services
                        </span>
                    </div>

                </div>

            </div>


            {/* Filters */}
            <div className="services-filter-card">

                <div className="service-search">

                    <Search size={17} />

                    <input
                        type="text"
                        placeholder="Search service..."
                        value={search}
                        onChange={(e) => {
                            setSearch(e.target.value);
                            setPage(1);
                        }}
                    />

                </div>


                <div className="service-filter">

                    <select
                        value={category}
                        onChange={(e) => {
                            setCategory(e.target.value);
                            setPage(1);
                        }}
                    >

                        {categoryOptions.map(
                            (item) => (
                                <option
                                    key={item}
                                    value={item}
                                >
                                    {item === "ALL"
                                        ? "All Categories"
                                        : item}
                                </option>
                            )
                        )}

                    </select>

                </div>


                <div className="service-filter">

                    <select
                        value={status}
                        onChange={(e) => {
                            setStatus(e.target.value);
                            setPage(1);
                        }}
                    >

                        <option value="ALL">
                            All Status
                        </option>

                        <option value="ACTIVE">
                            Active
                        </option>

                        <option value="INACTIVE">
                            Inactive
                        </option>

                    </select>

                </div>


                <button
                    className="service-clear-btn"
                    onClick={clearFilters}
                >
                    <RotateCcw size={15} />
                    Clear
                </button>

            </div>


            {/* Table */}
            <div className="services-table-card">

                <div className="services-table-header">

                    <div>
                        <h3>
                            Service List
                        </h3>

                        <p>
                            Showing{" "}
                            {paginatedServices.length}{" "}
                            of{" "}
                            {filteredServices.length}{" "}
                            services
                        </p>
                    </div>

                </div>


                {/* Desktop Table */}
                <div className="services-table-wrapper">

                    <table className="services-table">

                        <thead>
                            <tr>
                                <th>SERVICE</th>
                                <th>CATEGORY</th>
                                <th>DESCRIPTION</th>
                                <th>PRICING</th>
                                <th>PRICE</th>
                                <th>STATUS</th>
                                <th>ACTION</th>
                            </tr>
                        </thead>

                        <tbody>

                            {paginatedServices.length > 0 ? (
                                paginatedServices.map(
                                    (service) => (

                                        <tr
                                            key={
                                                service.id
                                            }
                                        >

                                            <td>
                                                <div className="service-name-cell">

                                                    <div className="service-icon">
                                                        <Package
                                                            size={
                                                                16
                                                            }
                                                        />
                                                    </div>

                                                    <div>
                                                        <strong>
                                                            {
                                                                service.name
                                                            }
                                                        </strong>

                                                        <span>
                                                            {
                                                                service.id
                                                            }
                                                        </span>
                                                    </div>

                                                </div>
                                            </td>


                                            <td>
                                                <span className="service-category-badge">
                                                    {
                                                        service.category
                                                    }
                                                </span>
                                            </td>


                                            <td>
                                                <span className="service-description">
                                                    {
                                                        service.description
                                                    }
                                                </span>
                                            </td>


                                            <td>
                                                <span className="pricing-type">
                                                    {
                                                        service.pricingType
                                                    }
                                                </span>
                                            </td>


                                            <td>
                                                <strong className="service-price">
                                                    {
                                                        formatAmount(
                                                            service.price
                                                        )
                                                    }
                                                </strong>
                                            </td>


                                            <td>
                                                <span
                                                    className={`service-status ${
                                                        service.status ===
                                                        "ACTIVE"
                                                            ? "service-active"
                                                            : "service-inactive"
                                                    }`}
                                                >
                                                    {
                                                        service.status
                                                    }
                                                </span>
                                            </td>


                                            <td>
                                                <div className="service-actions">

                                                    <button
                                                        className="service-action-btn"
                                                        title="View"
                                                    >
                                                        <Eye
                                                            size={
                                                                15
                                                            }
                                                        />
                                                    </button>

                                                    <button
                                                        className="service-action-btn"
                                                        title="Edit"
                                                    >
                                                        <Edit3
                                                            size={
                                                                15
                                                            }
                                                        />
                                                    </button>

                                                </div>
                                            </td>

                                        </tr>
                                    )
                                )
                            ) : (
                                <tr>
                                    <td
                                        colSpan="7"
                                        className="no-services"
                                    >
                                        No services found
                                    </td>
                                </tr>
                            )}

                        </tbody>

                    </table>

                </div>


                {/* Mobile */}
                <div className="services-mobile-list">

                    {paginatedServices.length > 0 ? (
                        paginatedServices.map(
                            (service) => (

                                <div
                                    className="service-mobile-card"
                                    key={
                                        service.id
                                    }
                                >

                                    <div className="service-mobile-top">

                                        <div className="service-name-cell">

                                            <div className="service-icon">
                                                <Package
                                                    size={
                                                        16
                                                    }
                                                />
                                            </div>

                                            <div>
                                                <strong>
                                                    {
                                                        service.name
                                                    }
                                                </strong>

                                                <span>
                                                    {
                                                        service.id
                                                    }
                                                </span>
                                            </div>

                                        </div>

                                        <span
                                            className={`service-status ${
                                                service.status ===
                                                "ACTIVE"
                                                    ? "service-active"
                                                    : "service-inactive"
                                            }`}
                                        >
                                            {
                                                service.status
                                            }
                                        </span>

                                    </div>


                                    <div className="service-mobile-description">
                                        {
                                            service.description
                                        }
                                    </div>


                                    <div className="service-mobile-details">

                                        <div>
                                            <small>
                                                Category
                                            </small>

                                            <strong>
                                                {
                                                    service.category
                                                }
                                            </strong>
                                        </div>

                                        <div>
                                            <small>
                                                Pricing
                                            </small>

                                            <strong>
                                                {
                                                    service.pricingType
                                                }
                                            </strong>
                                        </div>

                                        <div>
                                            <small>
                                                Price
                                            </small>

                                            <strong>
                                                {
                                                    formatAmount(
                                                        service.price
                                                    )
                                                }
                                            </strong>
                                        </div>

                                    </div>


                                    <div className="service-mobile-actions">

                                        <button>
                                            <Eye
                                                size={
                                                    14
                                                }
                                            />
                                            View
                                        </button>

                                        <button>
                                            <Edit3
                                                size={
                                                    14
                                                }
                                            />
                                            Edit
                                        </button>

                                    </div>

                                </div>
                            )
                        )
                    ) : (
                        <div className="mobile-no-services">
                            No services found
                        </div>
                    )}

                </div>


                {/* Pagination */}
                <div className="services-pagination">

                    <div>
                        Page {currentPage} of{" "}
                        {totalPages}
                    </div>

                    <div className="services-pagination-controls">

                        <select
                            value={rowsPerPage}
                            onChange={(e) => {
                                setRowsPerPage(
                                    Number(
                                        e.target.value
                                    )
                                );
                                setPage(1);
                            }}
                        >
                            <option value="5">
                                5 / page
                            </option>

                            <option value="10">
                                10 / page
                            </option>

                            <option value="20">
                                20 / page
                            </option>
                        </select>

                        <button
                            disabled={
                                currentPage ===
                                1
                            }
                            onClick={() =>
                                setPage(
                                    (p) =>
                                        Math.max(
                                            1,
                                            p - 1
                                        )
                                )
                            }
                        >
                            <ChevronLeft
                                size={16}
                            />
                        </button>

                        <span>
                            {currentPage}
                        </span>

                        <button
                            disabled={
                                currentPage ===
                                totalPages
                            }
                            onClick={() =>
                                setPage(
                                    (p) =>
                                        Math.min(
                                            totalPages,
                                            p + 1
                                        )
                                )
                            }
                        >
                            <ChevronRight
                                size={16}
                            />
                        </button>

                    </div>

                </div>

            </div>

        </div>
    );
}